// =====================================================================
// src/screens/v2/WallPostCard.jsx
//
// Phase 2.5 / C10b — Single wall post card.
//
// Used in two places:
//   1. Merchant Wall tab — full post list (this commit, C10b)
//   2. Customer BusinessProfile wall sub-tab (C10c)
//
// Props:
//   post           the post object from Firestore (must have id)
//   variant        'merchant' | 'customer'  — controls action menu
//   onEdit         (post) => void   (merchant only)
//   onDelete       (post) => void   (merchant only)
//   onSpark        (post) => void   (placeholder for C10d)
//   onComment      (post) => void   (placeholder for C10d)
//   onShare        (post) => void   (placeholder for C10c — only renders when post has photos)
//   onTapPhoto     (photoUrl) => void  — opens ImageViewerModal
//
// Engagement counters (sparkCount + commentCount) render as 0 by default
// in C10b. C10d wires the actual interactivity.
// =====================================================================

import React, { useState, useRef } from 'react';
import { MoreVertical, Edit2, Trash2, Zap, MessageSquareDashed, Rocket, Flag, X } from 'lucide-react';
import { POST_TYPES } from '../../lib/v2/wallPosts';

// Session C1 — reasons a customer can report a business wall post for.
// Keys are stored on the report doc; the super-admin Reports page maps them
// to labels via labelForReason() (extended to cover these keys). Reused for
// both the customer BusinessProfile wall and BusinessWallPostDetail.
export const WALL_REPORT_REASONS = [
  { key: 'misleading',    label: 'Misleading or false information' },
  { key: 'inappropriate', label: 'Inappropriate or offensive content' },
  { key: 'scam',          label: 'Scam or fraud' },
  { key: 'prohibited',    label: 'Prohibited or restricted item' },
  { key: 'impersonation', label: 'Fake or impersonating business' },
  { key: 'other',         label: 'Something else' },
];

// Convert a Firestore Timestamp / Date / number to "Xh ago" style.
// Defensive: Firestore serverTimestamps are null briefly during write.
function ageString(ts) {
  if (!ts) return 'just now';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString();
}

export default function WallPostCard({
  post,
  variant = 'customer',
  // Phase 2.5 / C10d — when true, Zap renders filled in accent color
  // (matches V1 PostCard's `likedByMe` behaviour). Parent computes this
  // by querying sparks/{userId} for the visible post set.
  sparkedByMe = false,
  // Session 6c2b — when true (merchant variant, biz is inactive), hide
  // the MoreVertical menu button so Edit/Delete aren't reachable. Post
  // content, photos, and engagement counters stay visible. Customer
  // variant ignores this prop (menu only exists for merchant).
  disabled = false,
  onEdit,
  onDelete,
  onSpark,
  onComment,
  onShare,
  onTapPhoto,
  // Session C1 — when provided (customer variant, viewer is not the business
  // owner), a ⋯ menu appears offering "Report post". The handler receives
  // { reason, detail } and is expected to call submitBusinessReport. Parents
  // pass this only when the viewer is signed in and doesn't own the business,
  // so the card itself doesn't need owner/identity logic.
  onReport = null,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  // Session C1 — customer report flow: bottom sheet -> reason picker.
  // 'sheet' = the Report/Cancel action sheet; 'reasons' = the reason list.
  const [reportStage, setReportStage] = useState(null); // null | 'sheet' | 'reasons'
  const [reportDetail, setReportDetail] = useState('');
  const [reportReason, setReportReason] = useState(null);
  const [reportBusy, setReportBusy] = useState(false);

  const canReport = variant === 'customer' && typeof onReport === 'function';

  async function handleSubmitReport() {
    if (!reportReason || reportBusy) return;
    setReportBusy(true);
    try {
      await onReport({ reason: reportReason, detail: reportDetail });
    } finally {
      setReportBusy(false);
      setReportStage(null);
      setReportReason(null);
      setReportDetail('');
    }
  }

  // ---- Item #9 — photo carousel state ----
  // Scroll-snap carousel: one photo visible at a time, horizontal
  // swipe between them, dots indicate position. Saves Storage
  // bandwidth vs rendering all photos in a vertical stack — only
  // the first slide loads eagerly; the others have loading="lazy"
  // so the browser fetches them when the user scrolls in. For
  // single-photo posts the dots row is hidden — the container is
  // the same code path so we don't fork rendering by photo count.
  const scrollerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  function handleScroll(e) {
    const w = e.currentTarget.clientWidth;
    if (!w) return;
    const idx = Math.round(e.currentTarget.scrollLeft / w);
    if (idx !== activeIndex) setActiveIndex(idx);
  }

  function jumpTo(i) {
    const sc = scrollerRef.current;
    if (!sc) return;
    sc.scrollTo({ left: sc.clientWidth * i, behavior: 'smooth' });
  }

  const typeDef = POST_TYPES.find(t => t.id === post.type) || POST_TYPES[3];
  const photos = Array.isArray(post.photos) ? post.photos.filter(p => p) : [];
  const hasPhotos = photos.length > 0;
  const isEdited = !!post.editedAt;

  return (
    <div className="rounded-2xl p-4 mb-3 relative"
         style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      {/* Header: type badge + age + menu */}
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium"
                style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}>
            <span>{typeDef.emoji}</span>
            {typeDef.label}
          </span>
          <span className="text-[11px]" style={{ color: 'var(--ink-soft)' }}>
            {ageString(post.createdAt)}{isEdited ? ' · edited' : ''}
          </span>
        </div>
        {variant === 'merchant' && !disabled && (
          <div className="relative">
            <button onClick={() => setMenuOpen(o => !o)}
                    className="w-8 h-8 rounded-full flex items-center justify-center active:scale-95"
                    aria-label="Post options">
              <MoreVertical size={16} style={{ color: 'var(--ink-soft)' }} />
            </button>
            {menuOpen && (
              <>
                {/* Click-outside backdrop */}
                <div onClick={() => setMenuOpen(false)}
                     style={{ position: 'fixed', inset: 0, zIndex: 50 }} />
                <div className="absolute right-0 top-9 rounded-xl shadow-lg overflow-hidden z-[51]"
                     style={{ background: 'var(--surface)', border: '1px solid var(--border)', minWidth: 140 }}>
                  <button onClick={() => { setMenuOpen(false); onEdit?.(post); }}
                          className="w-full px-3 py-2.5 text-left text-[13px] flex items-center gap-2 hover:bg-black/5"
                          style={{ color: 'var(--ink)' }}>
                    <Edit2 size={14} /> Edit
                  </button>
                  <button onClick={() => { setMenuOpen(false); onDelete?.(post); }}
                          className="w-full px-3 py-2.5 text-left text-[13px] flex items-center gap-2 hover:bg-black/5"
                          style={{ color: '#C45A3F', borderTop: '1px solid var(--border)' }}>
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        {canReport && (
          <button onClick={(e) => { e.stopPropagation(); setReportStage('sheet'); }}
                  className="w-8 h-8 rounded-full flex items-center justify-center active:scale-95"
                  aria-label="Post options">
            <MoreVertical size={16} style={{ color: 'var(--ink-soft)' }} />
          </button>
        )}
      </div>

      {/* Text */}
      {post.text && (
        <p className="text-[14px] leading-snug whitespace-pre-wrap mb-3"
           style={{ color: 'var(--ink)' }}>
          {post.text}
        </p>
      )}

      {/* Photos — Item #9 carousel.
          Container is a horizontal scroll-snap strip; each photo is a
          full-width child snap target rendered at object-fit: contain
          so the full image shows (no crop). The container is fixed
          aspect 4:3 so multi-photo carousels stay layout-predictable;
          letterbox bars fill the space when a photo's aspect doesn't
          match. Tap any photo to open ImageViewerModal at true full
          resolution — already wired in WallTab / BusinessProfile /
          BusinessWallPostDetail. Native scroll handles swipe; no
          third-party carousel library needed. */}
      {hasPhotos && (
        <div className="mb-3">
          {/* Hide native scrollbars on Chrome/Safari/Edge. Firefox is
              handled by scrollbarWidth: 'none' below. The selector is
              namespaced so we don't affect other scrollers. */}
          <style>{`.v2wp-photos-scroller::-webkit-scrollbar{display:none}`}</style>
          <div
            ref={scrollerRef}
            onScroll={handleScroll}
            className="v2wp-photos-scroller flex overflow-x-auto rounded-xl"
            style={{
              scrollSnapType: 'x mandatory',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
              aspectRatio: '4 / 3',
              background: 'var(--bg)',
              border: '1px solid var(--border)',
            }}
          >
            {photos.map((url, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); onTapPhoto?.(url); }}
                className="shrink-0 w-full block overflow-hidden active:scale-[0.99] transition-transform"
                style={{ scrollSnapAlign: 'start', background: 'var(--bg)' }}
                aria-label={photos.length > 1 ? `Photo ${i + 1} of ${photos.length}` : 'Photo'}
              >
                <img
                  src={url}
                  alt=""
                  loading={i === 0 ? 'eager' : 'lazy'}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              </button>
            ))}
          </div>

          {/* Dots indicator — only when multiple photos. Tappable so
              users on devices without natural horizontal-swipe (e.g.
              mouse-driven desktop) can jump between slides. */}
          {photos.length > 1 && (
            <div className="flex justify-center gap-1.5 mt-2">
              {photos.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); jumpTo(i); }}
                  aria-label={`Show photo ${i + 1}`}
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    background: i === activeIndex ? 'var(--ink)' : 'rgba(0,0,0,0.2)',
                    transition: 'background 200ms',
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Engagement row — matches V1 PostCard style (App.jsx ~L2462).
          Zap for sparks, MessageSquareDashed for comments, Rocket for
          share. Icons size 20, no labels, no top-border separator —
          just an icon-and-count row. Share only renders for posts with
          at least one photo (V1 parity + product spec). */}
      <div className="flex items-center gap-1 px-1 pt-1">
        <button onClick={(e) => { e.stopPropagation(); onSpark?.(post); }}
                className="flex items-center gap-1.5 px-2 py-1.5 rounded-full active:bg-black/5 active:scale-90 transition-transform"
                aria-label="Spark this post">
          <Zap size={20}
               style={{ color: sparkedByMe ? 'var(--accent)' : 'var(--ink)' }}
               fill={sparkedByMe ? 'var(--accent)' : 'none'}
               strokeWidth={1.6} />
          <span className="text-[13px]"
                style={{ color: sparkedByMe ? 'var(--accent)' : 'var(--ink-soft)' }}>
            {post.sparkCount || 0}
          </span>
        </button>
        <button onClick={(e) => { e.stopPropagation(); onComment?.(post); }}
                className="flex items-center gap-1.5 px-2 py-1.5 rounded-full active:bg-black/5 active:scale-90 transition-transform"
                aria-label="Comment on this post">
          <MessageSquareDashed size={20} style={{ color: 'var(--ink)' }} strokeWidth={1.6} />
          <span className="text-[13px]" style={{ color: 'var(--ink-soft)' }}>{post.commentCount || 0}</span>
        </button>
        {hasPhotos && (
          <button onClick={(e) => { e.stopPropagation(); onShare?.(post); }}
                  className="flex items-center gap-1.5 px-2 py-1.5 rounded-full active:bg-black/5 active:scale-90 transition-transform"
                  aria-label="Share this post">
            <Rocket size={20} style={{ color: 'var(--ink)' }} strokeWidth={1.6} />
          </button>
        )}
      </div>

      {/* Session C1 — report flow. Stage 1: action sheet (Report / Cancel).
          Stage 2: reason picker. Both are bottom sheets matching V1's
          PostCard menu (rounded-t-3xl + sheet-bottom-safe so the last row
          clears the phone nav + safe-area). stopPropagation on the shell so
          taps don't fall through to the card's onClick (which opens the post). */}
      {reportStage === 'sheet' && (
        <div className="absolute inset-0 z-40 flex items-end"
             style={{ background: 'rgba(0,0,0,0.5)' }}
             onClick={(e) => { e.stopPropagation(); setReportStage(null); }}>
          <div onClick={(e) => e.stopPropagation()}
               className="w-full rounded-t-3xl sheet-bottom-safe"
               style={{ background: 'var(--bg)' }}>
            <div className="w-12 h-1 rounded-full mx-auto my-3" style={{ background: 'var(--border)' }} />
            <button onClick={() => setReportStage('reasons')}
                    className="w-full flex items-center gap-3 px-5 py-3.5 active:bg-black/5">
              <Flag size={18} style={{ color: '#B33A3A' }} />
              <span className="text-[14px]" style={{ color: '#B33A3A' }}>Report post</span>
            </button>
            <button onClick={() => setReportStage(null)}
                    className="w-full flex items-center gap-3 px-5 py-3.5 active:bg-black/5"
                    style={{ borderTop: '1px solid var(--border)' }}>
              <X size={18} style={{ color: 'var(--ink-soft)' }} />
              <span className="text-[14px]" style={{ color: 'var(--ink-soft)' }}>Cancel</span>
            </button>
          </div>
        </div>
      )}

      {reportStage === 'reasons' && (
        <div className="absolute inset-0 z-40 flex items-end"
             style={{ background: 'rgba(0,0,0,0.5)' }}
             onClick={(e) => { e.stopPropagation(); if (!reportBusy) { setReportStage(null); setReportReason(null); } }}>
          <div onClick={(e) => e.stopPropagation()}
               className="w-full rounded-t-3xl sheet-bottom-safe"
               style={{ background: 'var(--bg)' }}>
            <div className="w-12 h-1 rounded-full mx-auto my-3" style={{ background: 'var(--border)' }} />
            <div className="px-5 pb-2 text-[13px] font-medium" style={{ color: 'var(--ink)' }}>
              Why are you reporting this post?
            </div>
            {WALL_REPORT_REASONS.map((r) => (
              <button key={r.key}
                      onClick={() => setReportReason(r.key)}
                      className="w-full flex items-center gap-3 px-5 py-3 active:bg-black/5"
                      style={{ borderTop: '1px solid var(--border)' }}>
                <span className="inline-block w-4 h-4 rounded-full shrink-0"
                      style={{
                        border: '2px solid ' + (reportReason === r.key ? 'var(--accent)' : 'var(--border)'),
                        background: reportReason === r.key ? 'var(--accent)' : 'transparent',
                      }} />
                <span className="text-[14px]" style={{ color: 'var(--ink)' }}>{r.label}</span>
              </button>
            ))}
            <div className="px-5 pt-3">
              <textarea
                value={reportDetail}
                onChange={(e) => setReportDetail(e.target.value)}
                placeholder="Add any detail (optional)"
                rows={2}
                maxLength={500}
                className="w-full rounded-xl px-3 py-2 text-[13px] resize-none"
                style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }}
              />
            </div>
            <div className="px-5 py-3 flex gap-2">
              <button onClick={() => { setReportStage(null); setReportReason(null); }}
                      disabled={reportBusy}
                      className="flex-1 py-2.5 rounded-xl text-[14px] active:scale-95"
                      style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }}>
                Cancel
              </button>
              <button onClick={handleSubmitReport}
                      disabled={!reportReason || reportBusy}
                      className="flex-1 py-2.5 rounded-xl text-[14px] active:scale-95"
                      style={{
                        background: (!reportReason || reportBusy) ? 'var(--border)' : '#B33A3A',
                        color: '#fff', opacity: (!reportReason || reportBusy) ? 0.7 : 1,
                      }}>
                {reportBusy ? 'Reporting…' : 'Submit report'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
